import React, { useState } from 'react';
import { FileTextIcon, DownloadIcon, NotebookPenIcon, BookmarkIcon, SendIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface CourseContentTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  module: number;
  lesson: number;
}
// Sample data for other users' notes
const otherUsersNotes = [{
  id: 1,
  author: {
    name: 'Marco Rossi',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Punti chiave sulle strategie ESG',
  content: '1. Le strategie ESG devono essere integrate nella strategia aziendale complessiva\n2. I fattori ambientali includono: emissioni, gestione delle risorse, biodiversità\n3. I fattori sociali riguardano: dipendenti, comunità, clienti\n4. La governance comprende: etica, trasparenza, conformità',
  date: '2 giorni fa',
  likes: 12
}, {
  id: 2,
  author: {
    name: 'Laura Bianchi',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Framework per la valutazione ESG',
  content: "Ho trovato particolarmente utile il framework presentato per valutare l'impatto ESG di un'iniziativa. I quattro passaggi chiave sono:\n1. Identificazione degli stakeholder\n2. Mappatura dei rischi e delle opportunità\n3. Definizione di KPI misurabili\n4. Implementazione e monitoraggio continuo",
  date: '5 giorni fa',
  likes: 8
}, {
  id: 3,
  author: {
    name: 'Alessandro Verdi',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Esempi pratici di strategie ESG',
  content: 'Gli esempi pratici presentati nella lezione sono stati illuminanti. In particolare:\n- Patagonia: integrazione dei fattori ambientali in tutta la supply chain\n- Unilever: programma di sostenibilità sociale con impatti misurabili\n- Danone: governance innovativa con status di B-Corp',
  date: '1 settimana fa',
  likes: 5
}];
// Sample personal notes
const personalNotesData = [{
  id: 1,
  title: "Domande per l'esame",
  content: "Quali sono i principali framework per la rendicontazione ESG? Come si valuta l'efficacia di una strategia ESG?",
  date: '3 giorni fa'
}];
const CourseContentTabs: React.FC<CourseContentTabsProps> = ({
  activeTab,
  onTabChange,
  module,
  lesson
}) => {
  const [personalNotes, setPersonalNotes] = useState(personalNotesData);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({
    title: '',
    content: ''
  });
  const [showAllOtherNotes, setShowAllOtherNotes] = useState(false);
  const handleAddNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) return;
    const newNoteItem = {
      id: Date.now(),
      title: newNote.title,
      content: newNote.content,
      date: 'Oggi'
    };
    setPersonalNotes([newNoteItem, ...personalNotes]);
    setNewNote({
      title: '',
      content: ''
    });
    setShowAddNote(false);
  };
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-779">
      {/* Tabs */}
      <div className="flex border-b border-gray-200" data-id="element-780">
        <button className={`px-4 py-3 text-sm font-medium flex-1 text-center ${activeTab === 'descrizione' ? 'text-custom-green border-b-2 border-custom-green' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => onTabChange('descrizione')} data-id="element-781">
          Descrizione
        </button>
        <button className={`px-4 py-3 text-sm font-medium flex-1 text-center ${activeTab === 'materiali' ? 'text-custom-green border-b-2 border-custom-green' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => onTabChange('materiali')} data-id="element-782">
          Materiali
        </button>
        <button className={`px-4 py-3 text-sm font-medium flex-1 text-center ${activeTab === 'note' ? 'text-custom-green border-b-2 border-custom-green' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => onTabChange('note')} data-id="element-783">
          Note
        </button>
      </div>

      {/* Tab content */}
      <div className="p-4" data-id="element-784">
        {activeTab === 'descrizione' && <div className="space-y-4" data-id="element-785">
            <h3 className="text-base font-medium text-gray-800" data-id="element-786">
              Obiettivi di apprendimento
            </h3>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2" data-id="element-787">
              <li data-id="element-788">Comprendere i principi fondamentali delle strategie ESG</li>
              <li data-id="element-789">
                Identificare i fattori chiave per implementare un approccio ESG
                efficace
              </li>
              <li data-id="element-790">
                Analizzare come i fattori ESG influenzano le decisioni aziendali
              </li>
              <li data-id="element-791">
                Valutare i vantaggi competitivi derivanti dall'implementazione
                di strategie ESG
              </li>
            </ul>
            <h3 className="text-base font-medium text-gray-800 pt-2" data-id="element-792">
              Descrizione della lezione
            </h3>
            <p className="text-sm text-gray-600" data-id="element-793">
              Questa lezione introduce il concetto di strategie ESG
              (Environmental, Social, Governance) e la loro importanza nel
              contesto dell'innovazione sostenibile. Esploreremo come le aziende
              possono integrare fattori ambientali, sociali e di governance
              nelle loro strategie di business per creare valore a lungo
              termine.
            </p>
            <p className="text-sm text-gray-600" data-id="element-794">
              Attraverso esempi concreti e case study, analizzeremo come le
              organizzazioni leader stanno implementando strategie ESG efficaci
              e quali risultati stanno ottenendo. Discuteremo anche delle sfide
              comuni e delle best practice per superarle.
            </p>
            <h3 className="text-base font-medium text-gray-800 pt-2" data-id="element-795">
              Prerequisiti
            </h3>
            <p className="text-sm text-gray-600" data-id="element-796">
              È consigliabile aver completato i moduli precedenti "Introduzione
              all'Innovazione Sostenibile" e "Modelli di Business Sostenibili"
              per una migliore comprensione dei concetti trattati in questa
              lezione.
            </p>
          </div>}

        {activeTab === 'materiali' && <div className="space-y-3" data-id="element-797">
            <div className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-798">
              <div className="mr-3 text-gray-500" data-id="element-799">
                <FileTextIcon size={20} data-id="element-800" />
              </div>
              <div className="flex-1" data-id="element-801">
                <div className="text-sm font-medium text-gray-800" data-id="element-802">
                  Slide della lezione - Strategie ESG
                </div>
                <div className="text-xs text-gray-500" data-id="element-803">
                  PDF • 24 pagine • 3.2 MB
                </div>
              </div>
              <button className="p-2 text-gray-500 hover:text-custom-green" data-id="element-804">
                <DownloadIcon size={18} data-id="element-805" />
              </button>
            </div>
            <div className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-806">
              <div className="mr-3 text-gray-500" data-id="element-807">
                <FileTextIcon size={20} data-id="element-808" />
              </div>
              <div className="flex-1" data-id="element-809">
                <div className="text-sm font-medium text-gray-800" data-id="element-810">
                  Case study - Implementazione ESG in azienda
                </div>
                <div className="text-xs text-gray-500" data-id="element-811">
                  PDF • 12 pagine • 1.8 MB
                </div>
              </div>
              <button className="p-2 text-gray-500 hover:text-custom-green" data-id="element-812">
                <DownloadIcon size={18} data-id="element-813" />
              </button>
            </div>
            <div className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-814">
              <div className="mr-3 text-gray-500" data-id="element-815">
                <FileTextIcon size={20} data-id="element-816" />
              </div>
              <div className="flex-1" data-id="element-817">
                <div className="text-sm font-medium text-gray-800" data-id="element-818">
                  Articolo - Trend ESG 2023
                </div>
                <div className="text-xs text-gray-500" data-id="element-819">
                  PDF • 8 pagine • 950 KB
                </div>
              </div>
              <button className="p-2 text-gray-500 hover:text-custom-green" data-id="element-820">
                <DownloadIcon size={18} data-id="element-821" />
              </button>
            </div>
            <div className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-822">
              <div className="mr-3 text-gray-500" data-id="element-823">
                <FileTextIcon size={20} data-id="element-824" />
              </div>
              <div className="flex-1" data-id="element-825">
                <div className="text-sm font-medium text-gray-800" data-id="element-826">
                  Esercitazione - Analisi ESG
                </div>
                <div className="text-xs text-gray-500" data-id="element-827">
                  DOCX • 5 pagine • 420 KB
                </div>
              </div>
              <button className="p-2 text-gray-500 hover:text-custom-green" data-id="element-828">
                <DownloadIcon size={18} data-id="element-829" />
              </button>
            </div>
          </div>}

        {activeTab === 'note' && <div className="space-y-4" data-id="element-830">
            {/* Personal Notes Section */}
            <div data-id="element-831">
              <div className="flex justify-between items-center mb-3" data-id="element-832">
                <h3 className="text-base font-medium text-gray-800" data-id="element-833">
                  Le tue note
                </h3>
                <button onClick={() => setShowAddNote(!showAddNote)} className="text-xs px-3 py-1.5 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors flex items-center" data-id="element-834">
                  {showAddNote ? <>
                      <ChevronUpIcon size={14} className="mr-1" data-id="element-835" />
                      Chiudi
                    </> : <>
                      <NotebookPenIcon size={14} className="mr-1" data-id="element-836" />
                      Nuova nota
                    </>}
                </button>
              </div>

              {/* Add note form */}
              {showAddNote && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4" data-id="element-837">
                  <div className="mb-3" data-id="element-838">
                    <input type="text" value={newNote.title} onChange={e => setNewNote({
                ...newNote,
                title: e.target.value
              })} placeholder="Titolo della nota" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-custom-green mb-2" data-id="element-839" />
                    <textarea value={newNote.content} onChange={e => setNewNote({
                ...newNote,
                content: e.target.value
              })} placeholder="Contenuto della nota..." className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-custom-green min-h-[100px] resize-none" data-id="element-840" />
                  </div>
                  <div className="flex justify-end" data-id="element-841">
                    <button onClick={() => setShowAddNote(false)} className="mr-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700" data-id="element-842">
                      Annulla
                    </button>
                    <button onClick={handleAddNote} className="px-3 py-1.5 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors text-sm flex items-center" data-id="element-843">
                      <SendIcon size={14} className="mr-1" data-id="element-844" />
                      Salva
                    </button>
                  </div>
                </div>}

              {/* Personal notes list */}
              {personalNotes.length === 0 ? <div className="text-center py-6 px-4 bg-gray-50 rounded-lg" data-id="element-845">
                  <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-846">
                    <NotebookPenIcon size={24} className="text-gray-400" data-id="element-847" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-800 mb-1" data-id="element-848">
                    Nessuna nota personale
                  </h4>
                  <p className="text-xs text-gray-500 max-w-xs mx-auto" data-id="element-849">
                    Prendi appunti durante la lezione per rivedere i concetti
                    chiave in seguito
                  </p>
                </div> : <div className="space-y-3" data-id="element-850">
                  {personalNotes.map(note => <div key={note.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-851">
                      <h4 className="text-sm font-medium text-gray-800 mb-1" data-id="element-852">
                        {note.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2 whitespace-pre-line" data-id="element-853">
                        {note.content}
                      </p>
                      <div className="flex justify-between items-center" data-id="element-854">
                        <span className="text-xs text-gray-500" data-id="element-855">
                          {note.date}
                        </span>
                        <div className="flex space-x-2" data-id="element-856">
                          <button className="text-xs text-gray-500 hover:text-gray-700" data-id="element-857">
                            Modifica
                          </button>
                          <button className="text-xs text-gray-500 hover:text-red-500" data-id="element-858">
                            Elimina
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>

            {/* Other Users' Notes Section */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-859">
              <div className="flex justify-between items-center mb-3" data-id="element-860">
                <h3 className="text-base font-medium text-gray-800" data-id="element-861">
                  Note condivise da altri studenti
                </h3>
                <button onClick={() => setShowAllOtherNotes(!showAllOtherNotes)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center" data-id="element-862">
                  {showAllOtherNotes ? <>
                      Mostra meno
                      <ChevronUpIcon size={14} className="ml-1" data-id="element-863" />
                    </> : <>
                      Mostra tutte
                      <ChevronDownIcon size={14} className="ml-1" data-id="element-864" />
                    </>}
                </button>
              </div>
              <div className="space-y-3" data-id="element-865">
                {(showAllOtherNotes ? otherUsersNotes : otherUsersNotes.slice(0, 2)).map(note => <div key={note.id} className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-866">
                    <div className="flex items-center mb-2" data-id="element-867">
                      <div className="w-6 h-6 rounded-full overflow-hidden mr-2" data-id="element-868">
                        <img src={note.author.avatar} alt={note.author.name} className="w-full h-full object-cover" data-id="element-869" />
                      </div>
                      <div className="flex-1" data-id="element-870">
                        <span className="text-sm font-medium text-gray-800" data-id="element-871">
                          {note.author.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-872">
                          {note.date}
                        </span>
                      </div>
                    </div>
                    <h4 className="text-sm font-medium text-gray-800 mb-1" data-id="element-873">
                      {note.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2 whitespace-pre-line" data-id="element-874">
                      {note.content}
                    </p>
                    <div className="flex justify-end items-center" data-id="element-875">
                      <button className="text-xs text-gray-500 hover:text-custom-green flex items-center" data-id="element-876">
                        <BookmarkIcon size={12} className="mr-1" data-id="element-877" />
                        Salva
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
};
export default CourseContentTabs;